import { Field, useFormikContext } from 'formik'

import { Box } from 'app/components/design-system-components'
import { InputField } from 'app/components/design-system-components/inputs'
import { SelectField } from 'app/components/design-system-components/inputs/Select/Select'
import {
  Heading,
  Text,
} from 'app/components/design-system-components/typography'
import {
  formFields,
  getPropertyArrangementValue,
  shouldBondBePaid,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/form.utils'
import {
  StyledFieldWrapper,
  StyledFlexBox,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/styled-form-component'
import type {
  PropertyArrangementOption,
  RentalHistoryFormValues,
} from 'app/profile/renter/refactor_rental_history/components/RentalHistoryForm/types'
import {
  isAddressHistoryOwned,
  isAddressHistoryTemporaryBnBArrangement,
  isAddressHistoryWithFamilyOrFriends,
} from 'app/sm/helpers'

interface ArrangementSectionProps {
  values: RentalHistoryFormValues
}

// Property arrangement options based on existing code (only options with show: true)
const PROPERTY_ARRANGEMENT_OPTIONS: PropertyArrangementOption[] = [
  {
    option: { value: 8, label: 'I rented with an agent' },
    optionMeta: { id: 8, displayText: 'I rented with an agent', value: 8 },
  },
  {
    option: { value: 9, label: 'I rented with private owner' },
    optionMeta: { id: 9, displayText: 'I rented with private owner', value: 9 },
  },
  {
    option: { value: 6, label: 'I sub-letted' },
    optionMeta: { id: 6, displayText: 'I sub-letted', value: 6 },
  },
  {
    option: { value: 2, label: 'I boarded' },
    optionMeta: { id: 2, displayText: 'I boarded', value: 2 },
  },
  {
    option: { value: 7, label: 'I lived with family/friends' },
    optionMeta: { id: 7, displayText: 'I lived with family/friends', value: 7 },
  },
  {
    option: { value: 5, label: 'I owned the property' },
    optionMeta: { id: 5, displayText: 'I owned the property', value: 5 },
  },
  {
    option: { value: 10, label: 'Temporary / BnB' },
    optionMeta: { id: 10, displayText: 'Temporary / BnB', value: 10 },
  },
]

/**
 * Property arrangement section component
 * Handles arrangement type, rent, and bond information
 */
export const ArrangementSection = ({ values }: ArrangementSectionProps) => {
  const { setFieldValue } = useFormikContext<RentalHistoryFormValues>()

  const ownedTheProperty = isAddressHistoryOwned(
    getPropertyArrangementValue(values.propertyArrangement),
  )
  const livedWithFamilyOrFriends = isAddressHistoryWithFamilyOrFriends(
    getPropertyArrangementValue(values.propertyArrangement),
  )
  const isTemporaryBnBArrangement = isAddressHistoryTemporaryBnBArrangement(
    getPropertyArrangementValue(values.propertyArrangement),
  )

  // Custom change handler for bond field that also updates isBondPaid
  const handleBondChange = (value: number) => {
    const shouldBeBondPaid = shouldBondBePaid(value)

    // Update both bond and isBondPaid fields
    setFieldValue(formFields.bond, value)
    setFieldValue(formFields.isBondPaid, shouldBeBondPaid)
  }

  // Custom change handler for property arrangement that updates reference contact fields
  const handlePropertyArrangementChange = (option: any) => {
    setFieldValue(formFields.propertyArrangement, option)

    const isOwned = isAddressHistoryOwned(getPropertyArrangementValue(option))
    const isTemporaryBnB = isAddressHistoryTemporaryBnBArrangement(
      getPropertyArrangementValue(option),
    )

    if (isOwned || isTemporaryBnB) {
      setFieldValue(formFields.shouldContactReference, false)
      setFieldValue(formFields.sendReferenceOnAdd, false)
      setFieldValue(formFields.isBondPaid, false)
    }
  }

  const showRentField =
    !values.isQLDForm22 &&
    getPropertyArrangementValue(values.propertyArrangement) !== 0 &&
    !ownedTheProperty

  const showBondField =
    !values.isQLDForm22 &&
    showRentField &&
    !livedWithFamilyOrFriends &&
    !isTemporaryBnBArrangement

  const showOptionalLabel =
    livedWithFamilyOrFriends || isTemporaryBnBArrangement

  return (
    <Box mt={7}>
      <Heading level={3} displayingLevel={3} mb={4}>
        Arrangement
      </Heading>
      <Text mb={7}>Add the applicant’s leasing details.</Text>

      {/* Property Arrangement Selection */}
      {!values.isQLDForm22 && (
        <StyledFlexBox gap={3} mb={7}>
          <Box flex={1}>
            <StyledFieldWrapper
              id={formFields.propertyArrangement}
              name={formFields.propertyArrangement}
              label="Arrangement"
              required
            >
              <Field name={formFields.propertyArrangement}>
                {({ field }: any) => (
                  <SelectField
                    {...field}
                    options={PROPERTY_ARRANGEMENT_OPTIONS}
                    label="Please select"
                    changeHandler={handlePropertyArrangementChange}
                  />
                )}
              </Field>
            </StyledFieldWrapper>
          </Box>
          <Box flex={1}></Box>
        </StyledFlexBox>
      )}

      {/* Rent Field */}
      {showRentField && (
        <StyledFlexBox gap={3} mb={7}>
          <Box flex={1}>
            <StyledFieldWrapper
              id={formFields.rate}
              name={formFields.rate}
              label={`Weekly Rent (AU$)${
                showOptionalLabel ? ' (optional)' : ''
              }`}
              required={!showOptionalLabel}
            >
              <Field name={formFields.rate}>
                {({ field }: any) => (
                  <InputField {...field} type="number" placeholder="e.g 200" />
                )}
              </Field>
            </StyledFieldWrapper>
          </Box>
          <Box flex={1}>
            {/* Bond Information */}
            {showBondField && (
              <StyledFieldWrapper
                id={formFields.bond}
                name={formFields.bond}
                label="Bond (AU$) optional"
              >
                <Field name={formFields.bond}>
                  {({ field }: any) => (
                    <InputField
                      {...field}
                      type="number"
                      placeholder="e.g 800"
                      changeHandler={handleBondChange}
                    />
                  )}
                </Field>
              </StyledFieldWrapper>
            )}
          </Box>
        </StyledFlexBox>
      )}
    </Box>
  )
}
